import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ChevronRight, Signal, PawPrint, Droplets } from "lucide-react";
import { useUnits, formatDistance } from "../lib/units";
import { coverImageFor } from "../lib/stockImages";
import { MudBadge } from "./MudBadge";

export default function BestWalkCard({ data }) {
  const { units } = useUnits();
  if (!data) return null;
  const loc = data.location;
  const ws = data.walk_score;

  return (
    <Link
      to={`/location/${loc.location_id}`}
      data-testid="best-walk-card"
      className="block relative overflow-hidden rounded-3xl border border-brand-border/60 shadow-sm bg-brand-primary text-brand-sand"
    >
      <div className="absolute inset-0 opacity-30">
        <img src={coverImageFor(loc)} alt="" className="w-full h-full object-cover" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-tr from-brand-primary via-brand-primary/85 to-brand-primary/55" />
      <div className="relative p-6 sm:p-7">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="td-section-label text-brand-accent/95 flex items-center gap-1.5">
            <Sparkles size={12} /> Today's best walk near you
          </span>
          {loc.ozzy_verified && (
            <span
              data-testid="best-walk-ozzy-verified"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-accent text-white border border-brand-accent"
              title="Personally walked & verified by Ozzy"
            >
              <PawPrint size={11} className="fill-white" /> Ozzy Verified
            </span>
          )}
        </div>
        <h2 className="font-heading text-2xl sm:text-3xl mt-3 leading-tight">{loc.name}</h2>
        <div className="text-brand-sand/80 mt-1 text-sm">{loc.region}</div>

        {/* Community signal chips */}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="best-walk-community-chips">
          <MudBadge avg={loc.mud_level_avg} count={loc.mud_reports_count} testid="best-walk-mud-badge" />
          {loc.water_source_available && (
            <span
              data-testid="best-walk-water-badge"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-sand/95 text-[#1E5B91] border border-brand-sand"
              title="Water source available along this trail"
            >
              <Droplets size={11} className="text-[#2E7AB8]" /> Water source
            </span>
          )}
          {loc.verified_walks_count > 0 && (
            <span
              data-testid="best-walk-paw-count"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand-sand/95 text-brand-primary border border-brand-sand"
              title={`${loc.verified_walks_count} walkers verified this trail`}
            >
              <PawPrint size={11} className="fill-brand-primary" /> {loc.verified_walks_count} Paw-Verified
            </span>
          )}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-4">
          <div>
            <div className="text-3xl font-heading font-semibold">{ws.emoji}</div>
            <div className="text-[11px] uppercase tracking-wider opacity-70 mt-1">Conditions</div>
            <div className="text-sm font-medium capitalize">{ws.tier}</div>
          </div>
          <div>
            {/* Personal match % when the Matchmaker has a profile to compare
                against; otherwise the legacy composite score so the card
                never renders "null / 100". */}
            {data.personalized && typeof data.match_percentage === "number" ? (
              <>
                <div
                  className="text-3xl font-heading font-semibold"
                  data-testid="best-walk-match-pct"
                >
                  {data.match_percentage}<span className="text-base opacity-70">%</span>
                </div>
                <div className="text-[11px] uppercase tracking-wider opacity-70 mt-1">Match</div>
                <div className="text-sm font-medium">For your pack</div>
              </>
            ) : (
              <>
                <div
                  className="text-3xl font-heading font-semibold"
                  data-testid="best-walk-total"
                >
                  {data.total}<span className="text-base opacity-70">/100</span>
                </div>
                <div className="text-[11px] uppercase tracking-wider opacity-70 mt-1">Total score</div>
                <div className="text-sm font-medium">Composite</div>
              </>
            )}
          </div>
          <div>
            <div className="text-3xl font-heading font-semibold flex items-center gap-1">
              {Array.from({ length: 4 }).map((_, i) => (
                <span key={i} className={`inline-block w-1.5 rounded-sm transition-all ${
                  i < data.bars ? "bg-brand-sand h-5" : "bg-brand-sand/30 h-2.5"
                }`} />
              ))}
            </div>
            <div className="text-[11px] uppercase tracking-wider opacity-70 mt-1">Distance</div>
            <div className="text-sm font-medium">{formatDistance(data.distance_km, { units, compact: true })}</div>
          </div>
        </div>

        <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-brand-sand text-brand-primary font-medium text-sm">
          Open this walk <ChevronRight size={16} />
        </div>
      </div>
    </Link>
  );
}
